import { Box, CircularProgress, Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { extraColors } from "../../utils/theme"
import { dayjsUTC } from "../../utils/dates"
import {
  DrawerSectionLabel,
  precomputedToggleGroupSx,
} from "./precomputedDrawerStyles"
import { V2_ACCENT, V2_FONT_MONO, V2_ON_ACCENT } from "./v2/biothreatV2Tokens"
import { useGdeltClusterMembers } from "./useGdeltClusterMembers"
import {
  backtestNowcast,
  bucketArrivals,
  classifyTrend,
  dedupeArrivalsByTimestamp,
  nowcastVolume,
  type TimeRange,
} from "./gdeltClusterAnalytics"
import { GdeltArrivalTimeline } from "./GdeltArrivalTimeline"
import { GdeltClusterTrendInsight } from "./GdeltClusterTrendInsight"
import { GdeltClusterNowcast } from "./GdeltClusterNowcast"

const DAY_MS = 86_400_000

type RangePreset = "all" | "30" | "14" | "7" | "3" | "1"

const PRESETS: { id: RangePreset; label: string; days: number | null }[] = [
  { id: "all", label: "All", days: null },
  { id: "30", label: "30D", days: 30 },
  { id: "14", label: "14D", days: 14 },
  { id: "7", label: "7D", days: 7 },
  { id: "3", label: "3D", days: 3 },
  { id: "1", label: "24HR", days: 1 },
]

/**
 * "Signal evolution" — how a GDELT cluster's reporting has accumulated over
 * time. Fetches the cluster's articles WITHOUT dedup (so every arrival counts),
 * then derives a deduped arrival timeline, a date-range-scoped trend, and a
 * projected current-day volume — all in-browser, memoized, and O(n).
 */
export function GdeltClusterEvolutionSection({
  clusterId,
}: {
  clusterId: string
}) {
  const { members, isLoading, isError } = useGdeltClusterMembers(
    clusterId,
    false
  )
  const [preset, setPreset] = useState<RangePreset>("all")
  // "Now" for the nowcast, captured once at mount. Date.now() is impure so it
  // can't be read during render — the lazy useState initializer is the sanctioned
  // place to snapshot it (re-mounts per cluster via the drawer's `key`).
  const [asOf] = useState(() => Date.now())

  const extent = useMemo(() => {
    let min = Infinity
    let max = -Infinity
    for (const member of members) {
      if (!member.publish_time) continue
      const ms = dayjsUTC(member.publish_time).valueOf()
      if (!Number.isFinite(ms)) continue
      if (ms < min) min = ms
      if (ms > max) max = ms
    }
    return Number.isFinite(min) ? { min, max } : null
  }, [members])

  const range = useMemo<TimeRange>(() => {
    if (!extent) return { start: null, end: null }
    const days = PRESETS.find(p => p.id === preset)?.days ?? null
    if (days == null) return { start: extent.min, end: extent.max }
    return {
      start: Math.max(extent.min, extent.max - days * DAY_MS),
      end: extent.max,
    }
  }, [extent, preset])

  const analytics = useMemo(() => {
    const nodes = dedupeArrivalsByTimestamp(members, range)
    const buckets = bucketArrivals(members, range)
    const trend = classifyTrend(buckets)
    const nowcast = nowcastVolume(buckets, asOf)
    const count = nodes.reduce((sum, node) => sum + node.count, 0)
    return { nodes, trend, nowcast, count }
  }, [members, range, asOf])

  // Dev-only frontend perf probe for these experimental rollups (see plan).
  // Kept out of the memo so render stays pure; the extra pass only runs in dev.
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const t0 = performance.now()
    const buckets = bucketArrivals(members, range)
    classifyTrend(buckets)
    dedupeArrivalsByTimestamp(members, range)
    console.debug(
      `[gdelt-evolution] ${members.length} articles → analytics in ${(
        performance.now() - t0
      ).toFixed(2)}ms`
    )
    // Leakage-free historical replay: does the nowcast actually predict, and
    // does it beat the old flat-mean baseline? Logs MAE / sMAPE / interval
    // coverage / skill for this cluster so it can be eyeballed in the console.
    const bt = backtestNowcast(buckets)
    if (bt.n > 0) {
      console.debug(
        `[gdelt-backtest] n=${bt.n} mae=${bt.mae.toFixed(2)} ` +
          `sMAPE=${bt.smape.toFixed(0)}% coverage=${(bt.coverage * 100).toFixed(
            0
          )}% (target ${(bt.targetCoverage * 100).toFixed(0)}%) ` +
          `skill=${(bt.skill * 100).toFixed(0)}% vs flat-mean ` +
          `(baselineMAE=${bt.baselineMae.toFixed(2)})`
      )
    }
  }, [members, range])

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }
  if (isError || !extent) return null

  const projectedLabel = analytics.nowcast.insufficient
    ? null
    : `~${analytics.nowcast.projectedToday} projected today`

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        <DrawerSectionLabel variant="caption" sx={{ mb: 0 }}>
          Signal evolution
        </DrawerSectionLabel>
        <RangePresetToggle preset={preset} onChange={setPreset} />
      </Box>

      <Typography
        sx={{
          fontFamily: V2_FONT_MONO,
          fontSize: "0.65rem",
          color: extraColors.translucent50,
          mb: 1,
        }}
      >
        {analytics.count} {analytics.count === 1 ? "article" : "articles"} in
        range
      </Typography>

      <GdeltArrivalTimeline
        // Re-mount on range change so the zoom/pan view resets to the new span.
        key={`${range.start ?? extent.min}-${range.end ?? extent.max}`}
        nodes={analytics.nodes}
        rangeStart={range.start ?? extent.min}
        rangeEnd={range.end ?? extent.max}
        projectedLabel={projectedLabel}
      />

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "wrap", mt: 2 }}
      >
        <GdeltClusterTrendInsight trend={analytics.trend} />
        <GdeltClusterNowcast nowcast={analytics.nowcast} />
      </Stack>
    </Box>
  )
}

function RangePresetToggle({
  preset,
  onChange,
}: {
  preset: RangePreset
  onChange: (preset: RangePreset) => void
}) {
  return (
    <Box
      role="radiogroup"
      aria-label="Timeline date range"
      sx={{
        display: "flex",
        border: `1px solid ${extraColors.translucent8}`,
        borderRadius: "6px",
        overflow: "hidden",
        ...precomputedToggleGroupSx,
      }}
    >
      {PRESETS.map(({ id, label }) => {
        const active = preset === id
        return (
          <Box
            key={id}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(id)}
            sx={{
              cursor: "pointer",
              border: "none",
              px: 1,
              py: 0.5,
              fontFamily: V2_FONT_MONO,
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              backgroundColor: active ? V2_ACCENT.cyan : "transparent",
              color: active ? V2_ON_ACCENT : extraColors.translucent50,
              "&:hover": { color: active ? V2_ON_ACCENT : extraColors.white },
            }}
          >
            {label}
          </Box>
        )
      })}
    </Box>
  )
}
